import React from 'react';

import { Control } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';

import type { TOriginSize } from '@vlabs/monokit/shared/ui/canvas';
import type { TRegionColors } from '@vlabs/monokit/shared/ui/regions-drawing';
import { RotateButtons } from '@vlabs/monokit/shared/ui/rotate-buttons';

import st from './ControlPanel.module.sass';
import * as ControlPanelControls from './controls';
import { Toast } from './Toast';
import type { TRegionRectangle, TRotationDeg } from '../types';

interface RectangleControlPanelProps {
  regionData?: TRegionRectangle;
  mediaSize?: TOriginSize;
  degree?: TRotationDeg;
  onChangeRegion?: (region: TRegionRectangle) => void;
  onChangeRotationDeg?: (rotationDeg: TRotationDeg) => void;
}

export function RectangleControlPanel({
  regionData = undefined,
  mediaSize = undefined,
  degree = undefined,
  onChangeRegion = undefined,
  onChangeRotationDeg = undefined,
}: RectangleControlPanelProps): React.JSX.Element {
  const { t } = useTranslation();

  const setFullFrame = () => {
    if (!mediaSize?.width || !mediaSize?.height) return;

    onChangeRegion?.({
      ...regionData as TRegionRectangle,
      x: 0,
      y: 0,
      width: mediaSize.width,
      height: mediaSize.height,
    });
  };

  return (
    <React.Fragment>
      <div className={st.UpperRow}>
        <Toast
          mediaSize={mediaSize}
          message={t('roi.modal.panel.info_text.zone', { context: 'edit' })}
        />

        <Control.Button
          kind="secondary"
          onClick={setFullFrame}
          size="small"
          variant="outlined"
        >
          {t('roi.modal.button.select_full_frame')}
        </Control.Button>

        <RotateButtons
          degree={degree}
          onChange={onChangeRotationDeg}
        />
      </div>

      <div className={st.LowerRow}>
        {!!regionData && (
          <React.Fragment>
            <ControlPanelControls.CoordinatesType
              onChange={(value: TMode) => onChangeRegion?.({ ...regionData, mode: value })}
              value={regionData.mode}
            />

            <div className={st.Separator} />
            <ControlPanelControls.ColorPicker
              onChange={(value: TRegionColors) => onChangeRegion?.({ ...regionData, color: value })}
              value={regionData.color}
            />

            <div className={st.Separator} />
            <ControlPanelControls.Rectangle
              mediaSize={mediaSize}
              onChange={onChangeRegion}
              region={regionData}
            />
          </React.Fragment>
        )}
      </div>
    </React.Fragment>
  );
}
